import React, { useState, useEffect } from 'react';
import {
    Tabs, Spin, Form, Button, Card, Tag, Row, Col, Input, notification, Space, Table, Tooltip, message
} from 'antd';
import 'antd/dist/antd.css';
import {
    StopOutlined,
    CheckOutlined
} from '@ant-design/icons';
import { useLocation, Redirect } from "react-router-dom";
import _ from 'lodash';
import moment from 'moment';
import * as Service from '../core/Service';
import NavAndSideFrame from '../components/NavAndSideFrame';
import { useTranslation } from 'react-i18next';
import { displayIsActive, displayVehicleType } from '../components/table/DisplayMethod';

const { TabPane } = Tabs;
const selectedKey = 2;
// const tableIdName = "good_product_id";

const DriverInfo = (props) => {
    const {t} = useTranslation();

    const location = useLocation();
    const [form] = Form.useForm();
    const [redirect, setRedirect] = useState(false);
    const [driverId, setDriverId] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isChangingIsActive, setChangingIsActive] = useState(false);
    const [driverData, setDriverData] = useState({});
    const [driverVehicleList, setDriverVehicleList] = useState([]);
    const [vehicleInfo, setVehicleInfo] = useState([]);

    const title = t('driver.driver_info');

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        let searchDriverId = searchParams.get("driver_id");
        setDriverId(searchDriverId);
    }, [location]);

    useEffect(() => {
        if (driverId === 0) return;
        getAllData();
    }, [driverId]);

    const getAllData = async () => {
        setLoading(true);
        try {
            let endpoint = `/api/driver/details?driver_id=${driverId}`;
            let result = await Service.call('GET', endpoint);
            if (result.status === -1
                && result.errorCode === -200001
                && driverId) {
                notification.error({ message: `Invalid Driver Account` });
                setRedirect(true);
            }
            let {
                driverInfo,
                driverVehicle,
                vehicle
            } = result;

            driverVehicle = _.orderBy(driverVehicle, `ctime`, `desc`);
            vehicle = _.orderBy(vehicle, `ctime`, `desc`);

            setDriverData(driverInfo);
            setDriverVehicleList(driverVehicle);
            setVehicleInfo(vehicle);
            console.log(result);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const setDriverVehicleTableHeader = () => {
        let columns = [
            {
                title: '操作',
                dataIndex: `driver_vehicle_id`,
                ellipsis: true,
                render: (value, record) => {
                    return (
                        <Tooltip title={record.is_active ? t('driver_vehicle.disable') : t('driver_vehicle.enable')}>
                            <Button
                                shape="circle"
                                style={{ color: record.is_active ? 'red' : 'green' }}
                                icon={record.is_active ? (<StopOutlined />) : (<CheckOutlined />)}
                                onClick={async () => {
                                    if (isChangingIsActive) return; // avoid multiple attempts
                
                                    setChangingIsActive(true);
                                    let { driver_vehicle_id, is_active } = record;
                                    is_active = !is_active;
                
                                    let result = await Service.call('put', `/api/driver_vehicle`, { driver_vehicle_id, is_active });
                                    setChangingIsActive(false);
                
                                    if (_.isEmpty(result) || result.status < 1) {
                                        return message.error(is_active ? t('driver_vehicle.fail_enable') : t('driver_vehicle.fail_disable'));
                                    }
                                    await getAllData();
                                    return message.success(is_active ? t('driver_vehicle.enabled') : t('driver_vehicle.disabled'));
                                }}
                            />
                        </Tooltip>
                    );
                }
            },
            {
                title: t('common.is_active'),
                dataIndex: `is_active`,
                ellipsis: true,
                render: (value) => displayIsActive(value, t)
            },
            {
                title: t('driver.driver_vehicle_id'),
                dataIndex: `driver_vehicle_id`,
                ellipsis: true,
                sorter: (a, b) => a.driver_vehicle_id - b.driver_vehicle_id
            },
            {
                title: t('vehicle.vehicle_id'),
                dataIndex: `vehicle_id`,
                ellipsis: true,
                sorter: (a, b) => a.vehicle_id - b.vehicle_id
            },
            {
                title: t('driver.driver_id'),
                dataIndex: `driver_id`,
                ellipsis: true,
                sorter: (a, b) => a.driver_id - b.driver_id
            },
            {
                title: t('common.ctime'),
                dataIndex: `ctime`,
                ellipsis: true,
                render: (value) => displayMoment(value),
                sorter: (a, b) => a.ctime - b.ctime
            },
            {
                title: t('common.utime'),
                dataIndex: `utime`,
                ellipsis: true,
                render: (value) => displayMoment(value),
                sorter: (a, b) => a.utime - b.utime
            },
        ];
        return columns;
    }

    const setVehicleInfoTableHeader = () => {
        let columns = [
            {
                title: t('vehicle.vehicle_id'),
                dataIndex: `vehicle_id`,
                ellipsis: true,
                sorter: (a, b) => a.vehicle_id - b.vehicle_id
            },
            {
                title: t('common.is_active'),
                dataIndex: `is_active`,
                ellipsis: true,
                render: (value) => displayIsActive(value, t),
                sorter: (a, b) => a.is_active - b.is_active
            },
            {
                title: t('vehicle.vehicle_license_no'),
                dataIndex: `vehicle_license_no`,
                ellipsis: true,
                sorter: (a, b) => a.vehicle_license_no - b.vehicle_license_no
            },
            {
                title: t('vehicle.vehicle_type'),
                dataIndex: `vehicle_type_id`,
                ellipsis: true,
                sorter: (a, b) => a.vehicle_type_id - b.vehicle_type_id,
                render: (value) => {
                  return displayVehicleType(value);
                }
            },
            {
                title: t('vehicle.seat_number'),
                dataIndex: `seat_number`,
                ellipsis: true,
                sorter: (a, b) => a.seat_number - b.seat_number
            },
            {
                title: t('vehicle.call_sign'),
                dataIndex: `call_sign`,
                ellipsis: true,
                sorter: (a, b) => a.call_sign - b.call_sign
            },
            {
                title: t('common.ctime'),
                dataIndex: `ctime`,
                ellipsis: true,
                render: (value) => displayMoment(value),
                sorter: (a, b) => a.ctime - b.ctime
            },
            {
                title: t('common.utime'),
                dataIndex: `utime`,
                ellipsis: true,
                render: (value) => displayMoment(value),
                sorter: (a, b) => a.utime - b.utime
            },
        ];
        return columns;
    }

    const displayMoment = (unixTime, outputFormat = 'YYYY/MM/DD HH:mm') => {
        if (unixTime !== 0) {
            let displayTime = moment.unix(unixTime).format(outputFormat);
            return displayTime;
        }
        return '-';
    }

    const tables = [
        {
            title: t('driver.driver_vehicle_list'),
            dataSource: driverVehicleList,
            columns: setDriverVehicleTableHeader(),
            rowKey: `driver_vehicle_id`,
        },
        {
            title: t('driver.vehicle_info'),
            dataSource: vehicleInfo,
            columns: setVehicleInfoTableHeader(),
            rowKey: `vehicle_id`,
        }
    ];

    const renderDriverInfoCard = () => {
        // if (_.isEmpty(userData)) return;
        let {
            driver_id,
            driver_name,
            email,
            mobile
        } = driverData;

        let tabs = [
            {
                tab: t('driver.basic_info'),
                row: [
                    { entry: t('driver.driver_id'), data: driver_id },
                    { entry: t('driver.driver_name'), data: driver_name },
                    { entry: t('driver.mobile'), data: mobile },
                    { entry: t('common.email'), data: email },
                ]
            }
        ];
        return (
            <Tabs defaultActiveKey={1}>
                {_.map(tabs, (tab_item, i) => {
                    return (
                        <TabPane tab={tab_item.tab} key={i}>
                            {_.map(tab_item.row, (row) => {
                                return (
                                    <>
                                        <Row>
                                            <Col xs={4} sm={4} md={4} lg={4} xl={4}>{row.entry}</Col>
                                            <Col>{row.data === "" ? "-" : row.data}</Col>
                                        </Row>
                                        <Row><Space /></Row>
                                    </>
                                );
                            })}
                        </TabPane>
                    )
                })}
            </Tabs>
        );
    }

    const renderUserDetail = () => {
        return (
            <>
                <Card
                    loading={loading}
                    title={
                        _.isEmpty(driverData)
                            ? null
                            : `${driverData.driver_name}`
                    }
                >
                    {_.isEmpty(driverData) ? null : renderDriverInfoCard()}
                </Card>

                {_.map(tables, (table) => {
                    return (
                        <Card title={table.title}>
                            <Spin spinning={loading}>
                                <Table
                                    pagination={{
                                        defaultPageSize: 25,
                                        // showSizeChanger: true,
                                        // pageSizeOptions: [25, 50, 100, 200, 500]
                                    }}
                                    scroll={{ x: true }}
                                    dataSource={table.dataSource}
                                    columns={table.columns}
                                    rowKey={table.rowKey}
                                />
                            </Spin>
                        </Card>
                    );
                })}
            </>
        );
    }
    const onFinish = (formData) => {
        let {
            driver_id
        } = formData;
        setDriverId(driver_id);
    }

    const renderForm = () => {
        return (
            <Form
                className="search-user-form col-12 mt-2"
                style={{ width: '100%' }}
                form={form}
                onFinish={onFinish}
            >
                <Form.Item
                    labelAlign="left"
                    name="user_id"
                    label="User ID"
                >
                    <Input
                        placeholder="User ID"
                    />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        {t("common.submit")}
                    </Button>
                </Form.Item>
            </Form>
        );
    }
    return (
        <NavAndSideFrame {...props} title={title} selectedKey={selectedKey}>
            {redirect ? <Redirect to="/driver_info" /> : null}
            {driverId ? renderUserDetail() : renderForm()}

        </NavAndSideFrame>
    )
}

export default DriverInfo;
